// src/dashboard/index.js — екран „Табло".
// Крос-връзки: фактурата се отваря през window.invoiceForm (legacy до
// миграцията на фактурите), задачата — през window.taskForm (вече модул).
//
// Подредбата следва maket-tablo.html: три карти отгоре, отдолу две колони
// 1.35fr/1fr — вляво графиката и най-големите неплатени, вдясно месечен
// мини-календар (точки вместо етикети) и „Следващите 7 дни". Плочките, които
// повтаряха дословно менюто, ги няма.

import { el, clear, clickableRow } from '../shared/dom.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, statCard, invoiceOverdue } from '../shared/ui.js';
import { state, cq } from '../shared/state.js';
import { openAt } from '../calendar/index.js';
import { noCompanyBanner } from '../shared/noCompany.js';

const MONTHS = ['Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни', 'Юли',
  'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'];
const MN = ['Яну', 'Фев', 'Мар', 'Апр', 'Май', 'Юни', 'Юли', 'Авг', 'Сеп', 'Окт', 'Ное', 'Дек'];

// Видовете събития — същите цветове като големия календар (KINDS в
// src/calendar/index.js). Точката е цвят без текст, затова всяка клетка носи
// и aria-label/title с думи — цветът не е единственият носител.
const KIND_META = {
  'invoice-due': { label: 'падеж', plural: 'падежа', color: 'red' },
  task: { label: 'задача', plural: 'задачи', color: 'amber' },
  'checklist-due': { label: 'чек лист', plural: 'чек лист', color: 'purple' },
  'vehicle-doc': { label: 'автопарк', plural: 'автопарк', color: 'teal' },
  event: { label: 'събитие', plural: 'събития', color: 'blue' },
  note: { label: 'бележка', plural: 'бележки', color: 'blue' },
};
const kindMeta = (ev) => KIND_META[ev.kind] || KIND_META[ev.source] || KIND_META.note;

const pad2 = (n) => (n < 10 ? '0' : '') + n;
const iso = (y, m, d) => y + '-' + pad2(m + 1) + '-' + pad2(d);
// Местна дата, не UTC — нощем toISOString() връща вчерашния ден.
const todayIso = () => { const n = new Date(); return iso(n.getFullYear(), n.getMonth(), n.getDate()); };

// Колко реда се побират — от височината на екрана, не заковано число.
function density() {
  const h = window.innerHeight;
  return {
    unpaid: h < 720 ? 3 : h < 810 ? 4 : h < 950 ? 5 : h < 1100 ? 6 : 7,
    up: h < 720 ? 2 : h < 950 ? 3 : 4,
  };
}

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);

  let invoices = [], contacts = [];
  // Двете заявки са независими — паралелно, не една след друга (преглед 17.09.2026).
  const [invR, conR] = await Promise.allSettled([api('/invoices' + cq()), api('/contacts' + cq())]);
  if (invR.status === 'fulfilled') invoices = invR.value; else toast(invR.reason && invR.reason.message, true);
  if (conR.status === 'fulfilled') contacts = conR.value;
  const cName = (id) => { const c = contacts.find((x) => x.id === id); return c ? c.name : ''; };

  const now = new Date();
  const yr = now.getFullYear();
  const today = todayIso();
  let unpaidCount = 0, unpaidSum = 0, overdueCount = 0, paidSum = 0, paidCount = 0;
  let draftCount = 0;
  const byMonth = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const unpaidList = [];
  (invoices || []).forEach((i) => {
    const total = Number(i.total) || 0;
    const d2 = (i.issue_date || '').slice(0, 10);
    // Черновата още не е издадена — клиентът не я е получил и не дължи нищо.
    // Броена като „неплатена", тя надуваше дълга на таблото, а стара чернова
    // без падеж ставаше и „просрочена" (invoiceOverdue гледа датата).
    if (i.status === 'draft') { draftCount++; return; }
    if (i.status !== 'paid') {
      unpaidCount++; unpaidSum += total; unpaidList.push(i);
      // Правилото за „просрочена" е ЕДНО за цялата система (А.8) — същото
      // рисува червения етикет в списъка на фактурите.
      if (invoiceOverdue(i, today)) overdueCount++;
    } else {
      paidCount++;
      if (d2.slice(0, 4) === String(yr)) { paidSum += total; const m = parseInt(d2.slice(5, 7), 10) - 1; if (m >= 0 && m < 12) byMonth[m] += total; }
    }
  });
  unpaidList.sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0));

  // ---- Трите карти ----
  // Когато има просрочени, това е най-важният факт на екрана — картата
  // предупреждава с кант, а подтекстът е изречение в червено, не бележка.
  const unpaidSub = overdueCount > 0
    ? overdueCount + ' от ' + unpaidCount + ' са просрочени'
    : (unpaidCount ? unpaidCount + ' бр.' : 'няма неплатени');
  page.appendChild(el('div', { class: 'cards' },
    statCard('Неплатени фактури', money(unpaidSum, 'EUR'), unpaidSub, { warn: overdueCount > 0, subAlarm: overdueCount > 0 }),
    statCard('Платено (' + yr + ')', money(paidSum, 'EUR'), paidCount + ' платени фактури'),
    // Черновите не са издадени, но не бива да изчезват безследно — забравена
    // чернова е фактура, която клиентът така и не е получил.
    statCard('Издадени фактури', String((invoices || []).length - draftCount),
      draftCount ? 'за активната фирма · ' + draftCount + (draftCount === 1 ? ' чернова' : ' чернови') : 'за активната фирма')
  ));

  // ---- Двете колони ----
  const left = el('div');
  const right = el('div');
  page.appendChild(el('div', { class: 'dash-grid' }, left, right));

  // Графиката: платени по месеци.
  let maxM = 1; byMonth.forEach((v) => { if (v > maxM) maxM = v; });
  const chartTotal = byMonth.reduce((a, b) => a + (b || 0), 0);
  const chartPanel = el('div', { class: 'panel' },
    el('div', { class: 'panel-head' }, el('h2', { text: 'Платени суми по месеци — ' + yr })));
  const chartWrap = el('div', {
    class: 'dash-chart',
    role: 'img',
    'aria-label': 'Стълбовидна графика: платени суми по месеци за ' + yr + ' г. Общо ' + money(chartTotal, 'EUR') + '.',
  });
  // (В.9) „0k" за месец с реални 480 € лъже. Под 10 000 — цялата сума;
  // съкращаване чак при наистина големи числа („12,4 хил.").
  const barLabel = (v) => {
    if (!v) return '';
    if (v < 10000) return Math.round(v) + ' €';
    return (Math.round(v / 100) / 10).toFixed(1).replace('.', ',') + ' хил.';
  };
  byMonth.forEach((v, idx) => {
    const h = Math.round(v / maxM * 100);
    chartWrap.appendChild(el('div', { class: 'col' },
      el('div', { class: 'v', text: barLabel(v) }),
      el('div', { class: 'bar', style: 'height:' + h + '%;min-height:' + (v ? 4 : 0) + 'px;' + (v ? '' : 'background:var(--surface-2);') }),
      el('div', { class: 'm', text: MN[idx] })
    ));
  });
  chartPanel.appendChild(chartWrap);
  left.appendChild(chartPanel);

  // Най-големите неплатени — броят редове зависи от височината на екрана.
  const upPanel = el('div', { class: 'panel' },
    el('div', { class: 'panel-head' },
      el('h2', { text: 'Най-големи неплатени' }),
      unpaidCount ? el('button', {
        class: 'btn sm ghost', type: 'button',
        onClick: () => { state().view = 'invoices'; window.CRM.renderShell(); },
        text: 'Всички ' + unpaidCount + ' →',
      }) : null));
  const unpaidBody = el('tbody');
  if (!unpaidList.length) upPanel.appendChild(emptyState('✔', 'Няма неплатени фактури.'));
  else {
    upPanel.appendChild(el('table', {},
      el('thead', {}, el('tr', {},
        el('th', { scope: 'col', text: 'Клиент' }),
        el('th', { scope: 'col', text: 'Падеж' }),
        el('th', { scope: 'col', class: 'num', text: 'Сума' }))),
      unpaidBody));
  }
  left.appendChild(upPanel);

  function drawUnpaid() {
    clear(unpaidBody);
    unpaidList.slice(0, density().unpaid).forEach((i) => {
      const due = (i.due_date || '').slice(0, 10);
      const od = due && due < today;
      unpaidBody.appendChild(clickableRow(() => { window.invoiceForm(i, contacts); }, 'Отвори фактура № ' + i.number,
        el('td', { text: cName(i.contact_id) || ('№ ' + i.number) }),
        el('td', {}, due ? el('span', { class: 'tag ' + (od ? 'red' : 'gray'), text: dfmt(i.due_date) }) : '—'),
        el('td', { class: 'num', style: 'font-weight:600', text: money(Number(i.total) || 0, 'EUR') })
      ));
    });
  }
  drawUnpaid();

  // ---- Дясна колона: мини-календар + следващите 7 дни ----
  const y = now.getFullYear(), m = now.getMonth();
  const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const lead = (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7;
  const in7 = new Date(now); in7.setDate(in7.getDate() + 7);
  const to7 = iso(in7.getFullYear(), in7.getMonth(), in7.getDate());
  const from = iso(y, m, 1);
  // Един заявка покрива и решетката на месеца, и 7-дневния прозорец.
  const to = to7 > iso(y, m, daysInMonth) ? to7 : iso(y, m, daysInMonth);

  let calData = { holidays: [], events: [] };
  try { calData = await api('/calendar' + cq() + '&from=' + from + '&to=' + to); } catch (e) {}
  const holidayBy = {};
  (calData.holidays || []).forEach((h) => { holidayBy[h.date] = h; });
  const eventsBy = {};
  (calData.events || []).forEach((ev) => { (eventsBy[ev.date] = eventsBy[ev.date] || []).push(ev); });

  // Какво има в деня, с думи — за title и aria-label (точките са само цвят).
  function dayWords(list) {
    const cnt = {};
    list.forEach((ev) => { const k = kindMeta(ev); cnt[k.label] = (cnt[k.label] || 0) + 1; });
    return Object.keys(cnt).map((k) => {
      const meta = Object.values(KIND_META).find((x) => x.label === k);
      return cnt[k] + ' ' + (cnt[k] === 1 ? k : meta.plural);
    }).join(' · ');
  }

  const calPanel = el('div', { class: 'panel' },
    el('div', { class: 'panel-head' },
      el('h2', { text: MONTHS[m] + ' ' + y }),
      el('button', {
        class: 'btn sm ghost', type: 'button',
        onClick: () => { state().view = 'calendar'; window.CRM.renderShell(); },
        text: 'Календар →',
      })));
  const cal = el('div', { class: 'mini-cal' });
  const head = el('div', { class: 'mini-head', 'aria-hidden': 'true' });
  ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'].forEach((w) => head.appendChild(el('div', { text: w })));
  cal.appendChild(head);
  const grid = el('div', { class: 'mini-grid', role: 'grid', 'aria-label': 'Календар за ' + MONTHS[m].toLowerCase() + ' ' + y });
  for (let i = 0; i < lead; i++) grid.appendChild(el('div', { class: 'mini-day out', 'aria-hidden': 'true' }));
  for (let d = 1; d <= daysInMonth; d++) {
    const date = iso(y, m, d);
    const wd = (new Date(Date.UTC(y, m, d)).getUTCDay() + 6) % 7;
    const list = eventsBy[date] || [];
    const words = dayWords(list);
    const hol = holidayBy[date];
    const label = d + ' ' + MONTHS[m].toLowerCase() + (hol ? ', ' + (hol.name || 'почивен ден') : '') + (words ? ': ' + words : '');
    const cell = el('button', {
      class: 'mini-day' + ((wd >= 5 || hol) ? ' we' : '') + (date === today ? ' today' : ''),
      type: 'button', role: 'gridcell',
      title: words || (hol ? hol.name : ''),
      'aria-label': label,
      // Клик отваря големия календар на този месец.
      onClick: () => { openAt(date); state().view = 'calendar'; window.CRM.renderShell(); },
    }, String(d));
    if (list.length) {
      const marks = el('div', { class: 'marks', 'aria-hidden': 'true' });
      list.slice(0, 3).forEach((ev) => marks.appendChild(el('span', { class: 'm ' + kindMeta(ev).color })));
      cell.appendChild(marks);
    }
    grid.appendChild(cell);
  }
  cal.appendChild(grid);
  calPanel.appendChild(cal);
  calPanel.appendChild(el('div', { class: 'mini-legend' },
    el('span', {}, el('i', { class: 'm red', 'aria-hidden': 'true' }), 'падеж'),
    el('span', {}, el('i', { class: 'm amber', 'aria-hidden': 'true' }), 'задача'),
    el('span', {}, el('i', { class: 'm purple', 'aria-hidden': 'true' }), 'чек лист'),
    el('span', {}, el('i', { class: 'm teal', 'aria-hidden': 'true' }), 'автопарк'),
    el('span', {}, el('i', { class: 'm blue', 'aria-hidden': 'true' }), 'бележка')
  ));
  right.appendChild(calPanel);

  // Следващите 7 дни — от същия източник като календара.
  const upcoming = (calData.events || [])
    .filter((ev) => ev.date >= today && ev.date <= to7)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const upcPanel = el('div', { class: 'panel' },
    el('div', { class: 'panel-head' }, el('h2', { text: 'Следващите 7 дни' })));
  const upcBody = el('div', { class: 'upcoming' });
  if (!upcoming.length) upcPanel.appendChild(emptyState('✔', 'Няма срокове през следващите 7 дни.'));
  else upcPanel.appendChild(upcBody);
  right.appendChild(upcPanel);

  function drawUpcoming() {
    clear(upcBody);
    upcoming.slice(0, density().up).forEach((ev) => {
      const meta = kindMeta(ev);
      const row = el('div', {
        class: 'row', role: ev.link ? 'button' : null, tabindex: ev.link ? '0' : null,
        title: ev.title,
        onClick: ev.link ? () => { state().view = ev.link.view; window.CRM.renderShell(); } : null,
      },
        el('span', { class: 'when', text: dfmt(ev.date).slice(0, 5) }),
        el('span', { class: 'what', text: ev.title }),
        el('span', { class: 'tag ' + meta.color, text: meta.label })
      );
      if (ev.link) row.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); row.click(); } });
      upcBody.appendChild(row);
    });
  }
  drawUpcoming();

  // Преоразмеряване: броят редове се преизчислява. Слушателят се маха сам,
  // щом таблото вече не е на екрана (page се чисти при смяна на изгледа).
  let timer = null;
  function onResize() {
    if (!document.contains(unpaidBody) && !document.contains(upcBody)) {
      window.removeEventListener('resize', onResize);
      return;
    }
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { drawUnpaid(); drawUpcoming(); }, 150);
  }
  window.addEventListener('resize', onResize);

  // Заварени записи без фирма — лента най-горе само за админа и само докато
  // ги има. Не се чака: таблото не бива да зависи от нея.
  noCompanyBanner(page, () => view(page));
}
